/**
 * ============================================================
 * AdminPubForm.js — Create/Edit Publication (Admin Panel)
 * ============================================================
 *
 * Modes:
 *   - NEW:  /admin/publications/new
 *   - EDIT: /admin/publications/:id
 *
 * Loads:
 *   - Publication data (GET /api/publications/:id)
 *   - All people (GET /api/people)
 *   - All projects (GET /api/projects)
 *
 * Saves:
 *   - Create publication (POST /api/publications)
 *   - Update publication (PUT /api/publications/:id)
 *   - Delete publication (DELETE /api/publications/:id)
 *
 * Notes:
 *   - Uses authFetch() for auto-redirect on token expiry
 **/

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";
import ItemList from "../components/ListItem";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";
import "./css/AdminForm.css";

import { pubPdf } from "../../utils/mediaPaths";


// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminPubForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = id && id !== "new";

  // Publication state
  const [publication, setPublication] = useState({
    title: "",
    year: "",
    pubtype_id: "",
    citation: "",
    abstract: "",
    doiurl: "",
    pdf: "",
    projects: [],
    people: [],
  });

  // File upload
  const [file, setFile] = useState(null);

  // Lookups
  const [pubTypes, setPubTypes] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  // Search inputs for add lists
  const [searchPeople, setSearchPeople] = useState("");
  const [searchProjects, setSearchProjects] = useState("");

  // Delete modal
  const [showDelete, setShowDelete] = useState(false);

  // ------------------------------------------------------------
  // Validate token on initial page load
  // ------------------------------------------------------------
  useEffect(() => {
    async function validateToken() {
      try {
        await authFetch("/api/authenticate");
        // If token is valid, nothing happens
      } catch (err) {
        // authFetch already handles redirect for 401/403
        console.error("Token validation failed:", err);
      }
    }
    validateToken();
  }, []);

  // ------------------------------------------------------------
  // Utility: always return an array
  // ------------------------------------------------------------
  function safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  // ------------------------------------------------------------
  // Filter people/projects to add (avoid duplicates)
  // ------------------------------------------------------------

  // Filtered list of people
  const filteredPeople = useMemo(() => {
    const s = searchPeople.toLowerCase().trim();
    if (!s) return [];

    const matches = allPeople.filter(
      (p) =>
        p.firstname.toLowerCase().includes(s) ||
        p.lastname.toLowerCase().includes(s)
    );

    return matches.filter(
      (p) => !publication.people.some((x) => x.id === p.id)
    );
  }, [searchPeople, allPeople, publication.people]);

  // Filtered list of projects
  const filteredProjects = useMemo(() => {
    const s = searchProjects.toLowerCase().trim();
    if (!s) return [];

    const matches = allProjects.filter((proj) =>
      (proj.name || "").toLowerCase().includes(s)
    );

    return matches.filter(
      (proj) => !publication.projects.some((x) => x.id === proj.id)
    );
  }, [searchProjects, allProjects, publication.projects]);

  // ------------------------------------------------------------
  // Loaders (publication data + lookups)
  // ------------------------------------------------------------
  async function loadPublication(editing, id, navigate, setPublication) {
    // Load publication data only if editing an existing publication
    if (!editing) return;

    try {
      const res = await authFetch(`/api/publications/${id}`);
      if (!res) return navigate("/admin/publications");

      const data = await res.json();

      const pubObj      = data.publicationData || {};
      const peopleArr   = safeArray(data.personData);
      const projectArr  = safeArray(data.projectData);

      setPublication({
        ...pubObj,
        people: peopleArr.map((p) => ({
          ...p,
          position: p.position ?? 0
        })),
        projects: projectArr.map((pr) => ({
          ...pr
        })),
      });

    } catch (err) {
      console.error("Failed to load publication:", err);
      navigate("/admin/publications");
    }
  }

  async function loadPubTypes(setPubTypes, setPublication, editing, publication) {
    try {
      // Load publication types
      const res = await authFetch("/api/publications/pubTypes");
      if (!res) return setPubTypes([]);

      const data = await res.json();
      const types = safeArray(data);
      setPubTypes(types);

      // Set default pub type for new publication
      if (!editing && types.length && !publication.pubtype_id) {
        setPublication((prev) => ({
          ...prev,
          pubtype_id: types[0].id,
        }));
      }
    } catch {
      setPubTypes([]);
    }
  }

  async function loadPeople(setAllPeople) {
    // Load people data as an array
    try {
      const res = await authFetch("/api/people/all");
      if (!res) return setAllPeople([]);
      const data = await res.json();
      setAllPeople(safeArray(data));
    } catch {
      setAllPeople([]);
    }
  }

  async function loadProjects(setAllProjects) {
    // Load project data as an array
    try {
      const res = await authFetch("/api/projects");
      if (!res) return setAllProjects([]);
      const data = await res.json();
      setAllProjects(safeArray(data));
    } catch {
      setAllProjects([]);
    }
  }

  // ------------------------------------------------------------
  // Load all data
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadData() {
      try {
        // Validate auth
        await authFetch("/api/authenticate");

        // Parallel-load all lookups
        await Promise.all([
          loadPubTypes(setPubTypes, setPublication, editing, publication),
          loadPeople(setAllPeople),
          loadProjects(setAllProjects),
        ]);

        // Load publication only after lookups are ready
        await loadPublication(editing, id, navigate, setPublication);

      } catch (err) {
        console.error("AdminPubForm load error:", err);
        if (editing) navigate("/admin/publications");
      }
    }

    loadData();
  }, [editing, id, navigate]);

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  function updateField(field, value) {
    setPublication((prev) => ({ ...prev, [field]: value }));
  }

  function handleInputChange(e) {
    const { name, value, files } = e.target;

    if (name === "pdf") {
      if (files && files[0]) setFile(files[0]);
      return;
    }

    updateField(name, value);
  }

  function removePdf() {
    setPublication((prev) => ({ ...prev, pdf: "" }));
    setFile(null);
  }

  // ---- Add/remove people ----
  function addPerson(person) {
    setPublication((prev) => ({
      ...prev,
      people: prev.people.concat({
        ...person,
        position: 0,
      }),
    }));
  }

  function removePerson(personId) {
    setPublication((prev) => ({
      ...prev,
      people: prev.people.filter((p) => p.id !== personId),
    }));
  }

  function updatePerson(personId, patch) {
    setPublication((prev) => ({
      ...prev,
      people: prev.people.map((p) =>
        p.id === personId ? { ...p, ...patch } : p
      ),
    }));
  }

  // ---- Add/remove projects ----
  function addProject(project) {
    setPublication((prev) => ({
      ...prev,
      projects: prev.projects.concat(project),
    }));
  }

  function removeProject(projectId) {
    setPublication((prev) => ({
      ...prev,
      projects: prev.projects.filter((p) => p.id !== projectId),
    }));
  }

  // ------------------------------------------------------------
  // Submit (create/update)
  // ------------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();

    try {
      const formData = new FormData();

      // pdf: new file wins, else existing
      if (file) formData.append("pdf", file);
      else formData.append("pdf", publication.pdf || "");

      const fields = [
        "title",
        "year",
        "pubtype_id",
        "citation",
        "abstract",
        "doiurl",
      ];
      fields.forEach((f) => formData.append(f, publication[f] ?? ""));

      formData.append("people", JSON.stringify(publication.people || []));
      formData.append("projects", JSON.stringify(publication.projects || []));

      const endpoint = editing
        ? `/api/publications/${id}`
        : `/api/publications`;
      const method = editing ? "PUT" : "POST";

      const res = await authFetch(endpoint, {
        method,
        body: formData,
      });

      if (!res) return;
      navigate("/admin/publications");
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save publication.");
    }
  }

  // ------------------------------------------------------------
  // Delete publication
  // ------------------------------------------------------------
  async function handleDelete() {
    try {
      const res = await authFetch(`/api/publications/${id}`, {
        method: "DELETE",
      });

      if (!res) return;

      setShowDelete(false);
      navigate("/admin/publications");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete publication.");
    }
  }

  // ----------------------------------------------------------
  // Get the pub path for download
  // ----------------------------------------------------------
  const pubSrc = pubPdf(publication.pdf);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="admin-wrapper">
      <SynlabAdminNavbar />

      <div className="admin-subpage-container">
        <div className="admin-page-header">
          <h1 className="admin-page-title">
            {editing ? "Edit Publication" : "Create Publication"}
          </h1>
        </div>

        <p className="admin-page-description">
          {editing
            ? "Update publication details, PDF, people, and projects."
            : "Create a new publication for the Synlab site."}
        </p>

        <form onSubmit={handleSubmit} className="admin-form">
          {/* ----- BASIC INFO ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Basic Info</h2>

            <div className="admin-form-grid">

              {/* ----- TITLE ----- */}
              <div className="admin-form-field">
                <label className="form-label">Title</label>
                <input
                  name="title"
                  className="form-control"
                  value={publication.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* ----- YEAR ----- */}
              <div className="admin-form-field">
                <label className="form-label">Year</label>
                <input
                  name="year"
                  className="form-control"
                  value={publication.year}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* ----- PUB TYPE ----- */}
              <div className="admin-form-field">
                <label className="form-label">Publication Type</label>
                <select
                  name="pubtype_id"
                  className="form-select"
                  value={publication.pubtype_id || ""}
                  onChange={handleInputChange}
                >

                  {/* ----- FALLBACK OPTION ----- */}
                  <option value="">-- Select Type --</option>

                  {safeArray(pubTypes).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ----- DOI URL ----- */}
              <div className="admin-form-field">
                <label className="form-label">DOI URL</label>
                <input
                  name="doiurl"
                  className="form-control"
                  value={publication.doiurl}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- CITATION ----- */}
              <div className="admin-form-field admin-form-field-wide">
                <label className="form-label">Citation</label>
                <textarea
                  name="citation"
                  className="form-control"
                  rows={4}
                  value={publication.citation}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- ABSTRACT ----- */}
              <div className="admin-form-field admin-form-field-wide">
                <label className="form-label">Abstract</label>
                <textarea
                  name="abstract"
                  className="form-control"
                  rows={8}
                  value={publication.abstract}
                  onChange={handleInputChange}
                />
              </div>

            </div>
          </div>

          {/* ----- PDF ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">PDF</h2>

            {publication.pdf && !file ? (
              <div className="admin-photo-row">
                <div>
                  <a href={pubSrc} target="_blank" rel="noreferrer">
                    Current PDF
                  </a>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={removePdf}
                >
                  Remove PDF
                </button>
              </div>
            ) : (
              <div className="admin-form-field">
                <label className="form-label">Upload PDF</label>
                <input
                  name="pdf"
                  type="file"
                  accept=".pdf"
                  className="form-control"
                  onChange={handleInputChange}
                />
              </div>
            )}
          </div>

          {/* ----- PEOPLE ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">People</h2>

            <div className="admin-form-field admin-form-field-wide">
              <label className="form-label">Search people to add</label>
              <input
                className="form-control"
                value={searchPeople}
                onChange={(e) => setSearchPeople(e.target.value)}
                placeholder="Search by name..."
              />
            </div>

            {/* ----- ADDABLE LIST ----- */}
            <div className="admin-add-list">
              {safeArray(filteredPeople).length === 0 ? (
                <div className="admin-empty-list">No people available</div>
              ) : (
                safeArray(filteredPeople).map((person) => (
                  <ItemList
                    key={person.id}
                    name={`${person.firstname} ${person.lastname}`}
                    isAdd
                    backgroundColor="#f0f0f0"
                    onClick={() => addPerson(person)}
                  />
                ))
              )}
            </div>

            {/* ----- SELECTED PEOPLE ----- */}
            <div className="admin-selected-list">
              {safeArray(publication.people).map((p) => (
                <ItemList
                  key={p.id}
                  name={`${p.firstname} ${p.lastname}`}
                  isDelete
                  onClick={() => removePerson(p.id)}
                  edit={
                    <div className="admin-inline-edit">
                      <label>Position</label>
                      <input
                        className="form-control form-control-sm"
                        value={p.position}
                        onChange={(e) =>
                          updatePerson(p.id, { position: e.target.value })
                        }
                      />
                    </div>
                  }
                />
              ))}
            </div>
          </div>

          {/* ----- PROJECTS ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Projects</h2>

            <div className="admin-form-field admin-form-field-wide">
              <label className="form-label">Search projects to add</label>
              <input
                className="form-control"
                value={searchProjects}
                onChange={(e) => setSearchProjects(e.target.value)}
                placeholder="Search by title..."
              />
            </div>

            {/* ----- ADDABLE LIST ----- */}
            <div className="admin-add-list">
              {safeArray(filteredProjects).length === 0 ? (
                <div className="admin-empty-list">No projects available</div>
              ) : (
                safeArray(filteredProjects).map((proj) => (
                  <ItemList
                    key={proj.id}
                    name={proj.name}
                    isAdd
                    backgroundColor="#f0f0f0"
                    onClick={() => addProject(proj)}
                  />
                ))
              )}
            </div>

            {/* ----- SELECTED PROJECTS ----- */}
            <div className="admin-selected-list">
              {safeArray(publication.projects).map((proj) => (
                <ItemList
                  key={proj.id}
                  name={proj.name}
                  isDelete
                  onClick={() => removeProject(proj.id)}
                />
              ))}
            </div>
          </div>

          {/* ----- BUTTONS ----- */}
          <div className="admin-form-buttons">
            <button className="btn btn-primary" type="submit">
              {editing ? "Save Changes" : "Create Publication"}
            </button>

            {editing && (
              <button
                className="btn btn-danger"
                type="button"
                onClick={() => setShowDelete(true)}
              >
                Delete Publication
              </button>
            )}

            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate("/admin/publications")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* ----- DELETE CONFIRM MODAL ----- */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Publication</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Deleting this publication cannot be undone. Continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <SynlabAdminFooter />
    </div>
  );
}

export default AdminPubForm;